<template>
  <div id="layout-wrapper">
    <NavigationBar/>

    <!-- Start right Content here -->
    <div id="content" class="content-minimize">
      <div class="page-content">
        <div class="container-fluid">

          <!-- Page header -->
          <div class="row mb-4">
            <div class="col-12">
              <div class="page-title-box d-sm-flex align-items-center justify-content-between">
                <h4 class="mb-sm-0 font-size-18">Employee Transfers</h4>
                <div class="page-title-right">
                  <ol class="breadcrumb m-0">
                    <li class="breadcrumb-item"><a href="javascript:void(0)">Home</a></li>
                    <li class="breadcrumb-item">HR Management</li>
                    <li class="breadcrumb-item active">Employee Transfers</li>
                  </ol>
                </div>
              </div>
            </div>
          </div>

          <!-- Stats Cards Row -->
          <div class="row">
            <!-- Total Transfers -->
            <div class="col-xl-3 col-md-6 mb-4">
              <div class="card border-left-primary shadow h-100 py-2">
                <div class="card-body">
                  <div class="d-flex align-items-center">
                    <div class="flex-shrink-0 me-3">
                      <div class="avatar-sm">
                        <span class="avatar-title bg-primary rounded-circle text-white font-size-20">
                          <i class="mdi mdi-transfer font-size-24"></i>
                        </span>
                      </div>
                    </div>
                    <div class="flex-grow-1">
                      <h5 class="font-size-15 mb-1">Total Transfers</h5>
                      <h4 class="mb-0">{{ summary.totalTransfers || 0 }}</h4>
                      <span v-if="summary.currentMonthTransfers" class="text-muted font-size-12">
                        {{ summary.currentMonthTransfers }} this month
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Pending Transfers -->
            <div class="col-xl-3 col-md-6 mb-4">
              <div class="card border-left-warning shadow h-100 py-2">
                <div class="card-body">
                  <div class="d-flex align-items-center">
                    <div class="flex-shrink-0 me-3">
                      <div class="avatar-sm">
                        <span class="avatar-title bg-warning rounded-circle text-white font-size-20">
                          <i class="mdi mdi-clock-outline font-size-24"></i>
                        </span>
                      </div>
                    </div>
                    <div class="flex-grow-1">
                      <h5 class="font-size-15 mb-1">Pending Approvals</h5>
                      <h4 class="mb-0">{{ summary.pendingTransfers || 0 }}</h4>
                      <span class="text-warning font-size-12">Requires action</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Approved Transfers -->
            <div class="col-xl-3 col-md-6 mb-4">
              <div class="card border-left-success shadow h-100 py-2">
                <div class="card-body">
                  <div class="d-flex align-items-center">
                    <div class="flex-shrink-0 me-3">
                      <div class="avatar-sm">
                        <span class="avatar-title bg-success rounded-circle text-white font-size-20">
                          <i class="mdi mdi-check-circle-outline font-size-24"></i>
                        </span>
                      </div>
                    </div>
                    <div class="flex-grow-1">
                      <h5 class="font-size-15 mb-1">Approved</h5>
                      <h4 class="mb-0">{{ summary.approvedTransfers || 0 }}</h4>
                      <span class="text-success font-size-12">Successfully processed</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Rejected Transfers -->
            <div class="col-xl-3 col-md-6 mb-4">
              <div class="card border-left-danger shadow h-100 py-2">
                <div class="card-body">
                  <div class="d-flex align-items-center">
                    <div class="flex-shrink-0 me-3">
                      <div class="avatar-sm">
                        <span class="avatar-title bg-danger rounded-circle text-white font-size-20">
                          <i class="mdi mdi-close-circle-outline font-size-24"></i>
                        </span>
                      </div>
                    </div>
                    <div class="flex-grow-1">
                      <h5 class="font-size-15 mb-1">Rejected</h5>
                      <h4 class="mb-0">{{ summary.rejectedTransfers || 0 }}</h4>
                      <span class="text-danger font-size-12">Not processed</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Filters and Actions Row -->
          <div class="row mb-4">
            <div class="col-12">
              <div class="card">
                <div class="card-body">
                  <div class="d-flex flex-wrap align-items-center mb-3">
                    <h5 class="card-title me-3 mb-3 mb-md-0">Employee Transfers</h5>
                    <div class="ms-auto">
                      <button @click="refreshData" class="btn btn-primary me-2">
                        <i class="mdi mdi-refresh me-1"></i> Refresh
                      </button>
                      <button @click="openNewTransferModal" class="btn btn-success">
                        <i class="mdi mdi-plus me-1"></i> New Transfer
                      </button>
                    </div>
                  </div>

                  <div class="row g-3">
                    <div class="col-md-3">
                      <label class="form-label">Status</label>
                      <select v-model="filters.status" class="form-select" @change="filterTransfers">
                        <option value="">All Statuses</option>
                        <option value="Pending">Pending</option>
                        <option value="Approved">Approved</option>
                        <option value="Rejected">Rejected</option>
                      </select>
                    </div>

                    <div class="col-md-3">
                      <label class="form-label">From Date</label>
                      <input type="date" v-model="filters.fromDate" class="form-control" @change="filterTransfers">
                    </div>

                    <div class="col-md-3">
                      <label class="form-label">To Date</label>
                      <input type="date" v-model="filters.toDate" class="form-control" @change="filterTransfers">
                    </div>

                    <div class="col-md-3">
                      <label class="form-label">Search</label>
                      <div class="position-relative">
                        <input
                            type="text"
                            v-model="filters.search"
                            class="form-control"
                            placeholder="Search employee or department..."
                            @input="debouncedSearch"
                        >
                        <i class="bx bx-search-alt search-icon position-absolute top-50 end-0 translate-middle-y me-2"></i>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Transfers Table -->
          <div class="row">
            <div class="col-12">
              <div class="card">
                <div class="card-body">
                  <!-- Loading indicator -->
                  <div v-if="loading" class="text-center p-5">
                    <div class="spinner-border text-primary" role="status">
                      <span class="visually-hidden">Loading...</span>
                    </div>
                    <p class="mt-2">Loading transfer data...</p>
                  </div>

                  <!-- No data state -->
                  <div v-else-if="!transfers.length" class="text-center p-5">
                    <i class="mdi mdi-database-search font-size-48 text-muted"></i>
                    <h5 class="mt-2">No transfer records found</h5>
                    <p class="text-muted">Try adjusting your filters or create a new transfer request</p>
                    <button @click="resetFilters" class="btn btn-sm btn-outline-primary mt-2">Reset Filters</button>
                  </div>

                  <!-- Transfers data table -->
                  <div v-else class="table-responsive">
                    <table class="table table-centered table-hover table-bordered mb-0">
                      <thead class="table-light">
                      <tr>
                        <th>Employee</th>
                        <th>From Department</th>
                        <th>To Department</th>
                        <th>Transfer Date</th>
                        <th>Status</th>
                        <th>Initiated By</th>
                        <th class="text-center">Actions</th>
                      </tr>
                      </thead>
                      <tbody>
                      <tr v-for="transfer in transfers" :key="transfer.id" :class="getRowClass(transfer.status)">
                        <td>
                          <div class="d-flex align-items-center">
                            <div class="avatar-xs me-2">
                                <span class="avatar-title rounded-circle bg-light text-body">
                                  {{ getInitials(transfer.employeeName) }}
                                </span>
                            </div>
                            <div>
                              <h5 class="font-size-14 mb-0">{{ transfer.employeeName }}</h5>
                              <p class="text-muted mb-0 font-size-12">{{ transfer.nationalId || 'ID not available' }}</p>
                            </div>
                          </div>
                        </td>
                        <td>{{ transfer.fromDepartmentName }}</td>
                        <td>{{ transfer.toDepartmentName }}</td>
                        <td>
                          {{ formatDate(transfer.transferDate) }}
                          <small v-if="transfer.effectiveDate && transfer.effectiveDate !== transfer.transferDate" class="d-block text-muted">
                            Effective: {{ formatDate(transfer.effectiveDate) }}
                          </small>
                        </td>
                        <td>
                            <span class="badge" :class="getStatusBadgeClass(transfer.status)">
                              {{ transfer.status }}
                            </span>
                        </td>
                        <td>
                          {{ transfer.initiatedBy }}
                          <small class="d-block text-muted">{{ formatDate(transfer.createdAt, true) }}</small>
                        </td>
                        <td class="text-center">
                          <button @click="viewTransferDetails(transfer)" class="btn btn-sm btn-info me-1">
                            <i class="mdi mdi-eye"></i>
                          </button>
                          <button
                              v-if="transfer.status === 'Pending'"
                              @click="approveTransfer(transfer)"
                              class="btn btn-sm btn-success me-1"
                              title="Approve Transfer"
                          >
                            <i class="mdi mdi-check"></i>
                          </button>
                          <button
                              v-if="transfer.status === 'Pending'"
                              @click="rejectTransfer(transfer)"
                              class="btn btn-sm btn-danger"
                              title="Reject Transfer"
                          >
                            <i class="mdi mdi-close"></i>
                          </button>
                        </td>
                      </tr>
                      </tbody>
                    </table>
                  </div>

                  <!-- Pagination controls -->
                  <div v-if="transfers.length && pagination.totalPages > 1" class="d-flex justify-content-between align-items-center mt-4">
                    <div>
                      Showing <span class="fw-medium">{{ transfers.length }}</span> of
                      <span class="fw-medium">{{ pagination.totalRecords }}</span> records
                    </div>
                    <ul class="pagination pagination-rounded mb-0">
                      <li class="page-item" :class="{ disabled: pagination.currentPage <= 1 }">
                        <a class="page-link" href="javascript:void(0)" @click="changePage(pagination.currentPage - 1)">
                          <i class="mdi mdi-chevron-left"></i>
                        </a>
                      </li>
                      <li
                          v-for="page in getPageNumbers()"
                          :key="page"
                          class="page-item"
                          :class="{ active: page === pagination.currentPage, disabled: page === '...' }"
                      >
                        <a v-if="page !== '...'" class="page-link" href="javascript:void(0)" @click="changePage(page)">
                          {{ page }}
                        </a>
                        <span v-else class="page-link">{{ page }}</span>
                      </li>
                      <li class="page-item" :class="{ disabled: pagination.currentPage >= pagination.totalPages }">
                        <a class="page-link" href="javascript:void(0)" @click="changePage(pagination.currentPage + 1)">
                          <i class="mdi mdi-chevron-right"></i>
                        </a>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- New Employee Transfer Modal -->
    <div class="modal fade" id="newTransferModal" tabindex="-1" aria-labelledby="newTransferModalLabel" aria-hidden="true">
      <div class="modal-dialog modal-lg">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title" id="newTransferModalLabel">Create Employee Transfer</h5>
            <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
          </div>
          <div class="modal-body">
            <form @submit.prevent="submitTransferForm">
              <!-- Employee Selection -->
              <div class="mb-3">
                <label class="form-label">Select Employee</label>
                <select v-model="transferForm.employeeId" class="form-select" required @change="loadEmployeeData">
                  <option value="">-- Select Employee --</option>
                  <option v-for="employee in employees" :key="employee.id" :value="employee.id">
                    {{ employee.firstName }} {{ employee.lastName }} - {{ employee.departmentName }}
                  </option>
                </select>
              </div>

              <!-- Employee details section (shown after employee selection) -->
              <div v-if="selectedEmployee" class="card mb-3 bg-light">
                <div class="card-body">
                  <div class="row">
                    <div class="col-md-6">
                      <h6 class="mb-2">Employee Details</h6>
                      <p class="mb-1"><strong>Name:</strong> {{ selectedEmployee.firstName }} {{ selectedEmployee.lastName }}</p>
                      <p class="mb-1"><strong>ID:</strong> {{ selectedEmployee.nationalId || 'N/A' }}</p>
                      <p class="mb-1"><strong>Position:</strong> {{ selectedEmployee.designation || 'N/A' }}</p>
                    </div>
                    <div class="col-md-6">
                      <h6 class="mb-2">Current Department</h6>
                      <p class="mb-1"><strong>Department:</strong> {{ selectedEmployee.departmentName || 'N/A' }}</p>
                      <p class="mb-1">
                        <strong>Salary:</strong>
                        <span
                            v-if="!showSalary && !getRight('VIEW SALARY')"
                            @click="showSalary = true"
                            class="salary-blur cursor-pointer"
                            title="Click to view"
                        >
                          ••••••••
                        </span>
                        <span v-else>{{ formatCurrency(selectedEmployee.salary,selectedEmployee.currency) }}</span>
                      </p>
                      <p class="mb-1"><strong>Join Date:</strong> {{ formatDate(selectedEmployee.hireDate) }}</p>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Target Department -->
              <div class="mb-3">
                <label class="form-label">Target Department</label>
                <select v-model="transferForm.toDepartmentId" class="form-select" required>
                  <option value="">-- Select Department --</option>
                  <option v-for="dept in departments" :key="dept.id" :value="dept.id"
                          :disabled="dept.id === selectedEmployee?.departmentID">
                    {{ dept.departmentName }}
                  </option>
                </select>
                <small v-if="selectedEmployee" class="text-muted">
                  Current department: {{ selectedEmployee.departmentName }}
                </small>
              </div>

              <div class="row">
                <div class="col-md-6">
                  <div class="mb-3">
                    <label class="form-label">Transfer Date</label>
                    <input type="date" v-model="transferForm.transferDate" class="form-control" required :min="today">
                  </div>
                </div>
                <div class="col-md-6">
                  <div class="mb-3">
                    <label class="form-label">Effective Date</label>
                    <input type="date" v-model="transferForm.effectiveDate" class="form-control" :min="transferForm.transferDate || today">
                    <small class="text-muted">Date when transfer takes effect</small>
                  </div>
                </div>
              </div>

              <!-- New Position and Salary -->
              <div class="row">
                <div class="col-md-6">
                  <div class="mb-3">
                    <label class="form-label">New Position (Optional)</label>
                    <input type="text" v-model="transferForm.newPosition" class="form-control" placeholder="Enter new position">
                  </div>
                </div>
                <div class="col-md-6">
                  <div class="mb-3">
                    <label class="form-label">New Salary (Optional)</label>
                    <div v-if="getRight('VIEW SALARY')">
                      <input type="number" v-model="transferForm.newSalary" class="form-control" placeholder="Enter new salary" min="0" step="0.01">
                    </div>
                    <div v-else>
                      <div class="d-flex align-items-center">
                        <input
                            type="number"
                            v-model="transferForm.newSalary"
                            :class="['form-control', {'salary-blur': !showFormSalary}]"
                            placeholder="Enter new salary"
                            min="0"
                            step="0.01"
                        >
                        <button
                            type="button"
                            class="btn btn-sm btn-outline-secondary ms-2"
                            @click="showFormSalary = !showFormSalary"
                        >
                          <i :class="showFormSalary ? 'mdi mdi-eye-off' : 'mdi mdi-eye'"></i>
                        </button>
                      </div>
                      <small class="text-muted">Click the eye icon to toggle salary visibility</small>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Reason for Transfer -->
              <div class="mb-3">
                <label class="form-label">Reason for Transfer</label>
                <textarea v-model="transferForm.reason" class="form-control" rows="3" required placeholder="Explain reason for transfer"></textarea>
              </div>

              <!-- Additional Comments -->
              <div class="mb-3">
                <label class="form-label">Additional Remarks (Optional)</label>
                <textarea v-model="transferForm.remarks" class="form-control" rows="2" placeholder="Additional comments or notes"></textarea>
              </div>
            </form>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-light" data-bs-dismiss="modal">Cancel</button>
            <button
                type="button"
                class="btn btn-primary"
                @click="submitTransferForm"
                :disabled="isSubmitting"
            >
              <span v-if="isSubmitting" class="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
              Create Transfer
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Transfer Details Modal -->
    <div class="modal fade" id="transferDetailsModal" tabindex="-1" aria-labelledby="transferDetailsModalLabel" aria-hidden="true">
      <div class="modal-dialog modal-lg">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title" id="transferDetailsModalLabel">Transfer Details</h5>
            <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
          </div>
          <div class="modal-body" v-if="selectedTransfer">
            <div class="row mb-4">
              <div class="col-md-6">
                <h5 class="font-size-15">{{ selectedTransfer.employeeName }}</h5>
                <p class="text-muted mb-1">ID: {{ selectedTransfer.nationalId || 'N/A' }}</p>
                <p class="badge" :class="getStatusBadgeClass(selectedTransfer.status)">
                  {{ selectedTransfer.status }}
                </p>
              </div>
              <div class="col-md-6 text-md-end">
                <p class="mb-1">Transfer Date: {{ formatDate(selectedTransfer.transferDate) }}</p>
                <p v-if="selectedTransfer.effectiveDate" class="mb-1">
                  Effective Date: {{ formatDate(selectedTransfer.effectiveDate) }}
                </p>
                <p class="mb-0">Requested: {{ formatDate(selectedTransfer.createdAt, true) }}</p>
              </div>
            </div>

            <div class="d-flex mb-4">
              <div class="flex-fill text-center p-3 bg-light me-2 rounded">
                <h6 class="mb-1">From Department</h6>
                <h5 class="mb-0">{{ selectedTransfer.fromDepartmentName }}</h5>
              </div>
              <div class="d-flex align-items-center justify-content-center">
                <i class="mdi mdi-arrow-right-bold font-size-24"></i>
              </div>
              <div class="flex-fill text-center p-3 bg-light ms-2 rounded">
                <h6 class="mb-1">To Department</h6>
                <h5 class="mb-0">{{ selectedTransfer.toDepartmentName }}</h5>
              </div>
            </div>

            <div class="row mb-3">
              <div class="col-md-6">
                <div class="card border h-100">
                  <div class="card-header bg-light py-2">
                    <h6 class="mb-0">Transfer Details</h6>
                  </div>
                  <div class="card-body">
                    <div v-if="selectedTransfer.newPosition" class="mb-2">
                      <strong>New Position:</strong> {{ selectedTransfer.newPosition }}
                    </div>
                    <div v-if="selectedTransfer.newSalary" class="mb-2">
                      <strong>New Salary:</strong>
                      <span
                          v-if="!showNewSalary && !getRight('VIEW SALARY')"
                          @click="showNewSalary = true"
                          class="salary-blur cursor-pointer"
                          title="Click to view"
                      >
                        ••••••••
                      </span>
                      <span v-else>{{ formatCurrency(selectedTransfer.newSalary,selectedTransfer.currency) }}</span>
                    </div>
                    <div class="mb-2">
                      <strong>Reason:</strong> {{ selectedTransfer.reason || 'No reason provided' }}
                    </div>
                    <div v-if="selectedTransfer.remarks" class="mb-0">
                      <strong>Remarks:</strong> {{ selectedTransfer.remarks }}
                    </div>
                  </div>
                </div>
              </div>
              <div class="col-md-6">
                <div class="card border h-100">
                  <div class="card-header bg-light py-2">
                    <h6 class="mb-0">Processing Information</h6>
                  </div>
                  <div class="card-body">
                    <div class="mb-2">
                      <strong>Initiated By:</strong> {{ selectedTransfer.initiatedBy }}
                      <small v-if="selectedTransfer.initiatorRole" class="text-muted">
                        ({{ selectedTransfer.initiatorRole }})
                      </small>
                    </div>

                    <div v-if="selectedTransfer.status === 'Approved'" class="mb-2">
                      <strong>Approved By:</strong> {{ selectedTransfer.approvedBy }}
                      <small v-if="selectedTransfer.approverRole" class="text-muted">
                        ({{ selectedTransfer.approverRole }})
                      </small>
                      <br>
                      <small v-if="selectedTransfer.approvalDate" class="text-muted">
                        on {{ formatDate(selectedTransfer.approvalDate, true) }}
                      </small>
                    </div>

                    <div v-if="selectedTransfer.status === 'Rejected'" class="mb-0">
                      <strong>Rejected By:</strong> {{ selectedTransfer.rejectedBy }}
                      <small v-if="selectedTransfer.rejectorRole" class="text-muted">
                        ({{ selectedTransfer.rejectorRole }})
                      </small>
                      <br>
                      <small v-if="selectedTransfer.rejectionDate" class="text-muted">
                        on {{ formatDate(selectedTransfer.rejectionDate, true) }}
                      </small>
                      <div v-if="selectedTransfer.rejectionReason" class="mt-2 p-2 bg-light rounded">
                        <strong>Rejection Reason:</strong><br>
                        {{ selectedTransfer.rejectionReason }}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Close</button>
            <button
                v-if="selectedTransfer && selectedTransfer.status === 'Pending'"
                type="button"
                class="btn btn-success me-2"
                @click="approveTransfer(selectedTransfer)"
            >
              Approve Transfer
            </button>
            <button
                v-if="selectedTransfer && selectedTransfer.status === 'Pending'"
                type="button"
                class="btn btn-danger"
                @click="rejectTransfer(selectedTransfer)"
            >
              Reject Transfer
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Approve Transfer Modal -->
    <div class="modal fade" id="approveTransferModal" tabindex="-1" aria-labelledby="approveTransferModalLabel" aria-hidden="true">
      <div class="modal-dialog">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title" id="approveTransferModalLabel">Approve Transfer</h5>
            <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
          </div>
          <div class="modal-body" v-if="selectedTransfer">
            <div class="text-center mb-4">
              <i class="mdi mdi-check-circle-outline text-success font-size-48"></i>
              <h5 class="mt-2">Confirm Transfer Approval</h5>
              <p class="text-muted">
                You are about to approve the transfer of <strong>{{ selectedTransfer.employeeName }}</strong>
                from <strong>{{ selectedTransfer.fromDepartmentName }}</strong>
                to <strong>{{ selectedTransfer.toDepartmentName }}</strong>.
              </p>
            </div>

            <div class="alert alert-info">
              <i class="mdi mdi-information-outline me-2"></i>
              Once approved, the employee's department will be updated immediately.
              <span v-if="selectedTransfer.newPosition || selectedTransfer.newSalary">
                <br>
                The employee's
                <span v-if="selectedTransfer.newPosition">position</span>
                <span v-if="selectedTransfer.newPosition && selectedTransfer.newSalary"> and </span>
                <span v-if="selectedTransfer.newSalary">salary</span>
                will also be updated.
              </span>
            </div>

            <div class="mb-3">
              <label class="form-label">Approval Remarks (Optional)</label>
              <textarea v-model="approvalForm.remarks" class="form-control" rows="3" placeholder="Add any additional comments"></textarea>
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-light" data-bs-dismiss="modal">Cancel</button>
            <button
                type="button"
                class="btn btn-success"
                @click="confirmApproveTransfer"
                :disabled="isProcessing"
            >
              <span v-if="isProcessing" class="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
              Approve Transfer
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Reject Transfer Modal -->
    <div class="modal fade" id="rejectTransferModal" tabindex="-1" aria-labelledby="rejectTransferModalLabel" aria-hidden="true">
      <div class="modal-dialog">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title" id="rejectTransferModalLabel">Reject Transfer</h5>
            <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
          </div>
          <div class="modal-body" v-if="selectedTransfer">
            <div class="text-center mb-4">
              <i class="mdi mdi-close-circle-outline text-danger font-size-48"></i>
              <h5 class="mt-2">Confirm Transfer Rejection</h5>
              <p class="text-muted">
                You are about to reject the transfer of <strong>{{ selectedTransfer.employeeName }}</strong>
                from <strong>{{ selectedTransfer.fromDepartmentName }}</strong>
                to <strong>{{ selectedTransfer.toDepartmentName }}</strong>.
              </p>
            </div>

            <div class="mb-3">
              <label class="form-label">Reason for Rejection <span class="text-danger">*</span></label>
              <textarea
                  v-model="rejectionForm.reason"
                  class="form-control"
                  rows="3"
                  placeholder="Provide reason for rejection"
                  required
              ></textarea>
              <div v-if="rejectionError" class="text-danger mt-1">
                {{ rejectionError }}
              </div>
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-light" data-bs-dismiss="modal">Cancel</button>
            <button
                type="button"
                class="btn btn-danger"
                @click="confirmRejectTransfer"
                :disabled="isProcessing"
            >
              <span v-if="isProcessing" class="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
              Reject Transfer
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
/* eslint-disable */
import { getRight, authUrl, execute } from "@/api";
import _ from 'lodash';
import NavigationBar from "@/components/Navigation.vue";

export default {
  name: "EmployeeTransfers",
  components: {NavigationBar},
  data() {
    return {
      loading: false,
      isSubmitting: false,
      isProcessing: false,
      showSalary: false,
      showNewSalary: false,
      showFormSalary: false,
      transfers: [],
      summary: {},
      pagination: {
        currentPage: 1,
        totalPages: 1,
        limit: 10,
        totalRecords: 0
      },
      filters: {
        status: '',
        search: '',
        fromDate: '',
        toDate: '',
        departmentId: ''
      },
      departments: [],
      employees: [],
      selectedTransfer: null,
      selectedEmployee: null,

      transferForm: {
        employeeId: '',
        fromDepartmentId: '',
        toDepartmentId: '',
        transferDate: new Date().toISOString().slice(0, 10), // Current date in YYYY-MM-DD
        effectiveDate: new Date().toISOString().slice(0, 10),
        reason: '',
        newPosition: '',
        newSalary: '',
        remarks: ''
      },

      approvalForm: {
        remarks: ''
      },

      rejectionForm: {
        reason: ''
      },
      rejectionError: ''
    };
  },

  computed: {
    today() {
      return new Date().toISOString().slice(0, 10);
    }
  },

  created() {
    this.debouncedSearch = _.debounce(this.filterTransfers, 500);

    // Get all required data when component is created
    this.getTransfers();
    this.getDepartments();
    this.getEmployees();
  },

  methods: {
    // Rights checking function
    getRight(type) {
      return false;
    },

    // Data fetching functions
    getTransfers() {
      this.loading = true;

      const data = new FormData();
      data.append("function", "getEmployeeTransfers");
      data.append("page", this.pagination.currentPage);
      data.append("limit", this.pagination.limit);

      if (this.filters.status) {
        data.append("status", this.filters.status);
      }

      if (this.filters.search) {
        data.append("search", this.filters.search);
      }

      if (this.filters.fromDate) {
        data.append("fromDate", this.filters.fromDate);
      }

      if (this.filters.toDate) {
        data.append("toDate", this.filters.toDate);
      }

      if (this.filters.departmentId) {
        data.append("departmentId", this.filters.departmentId);
      }

      execute(data, authUrl)
          .then((res) => {
            this.loading = false;

            if (res.data.success) {
              this.transfers = res.data.data.transfers || [];
              this.summary = res.data.data.summary || {};
              this.pagination = res.data.data.pagination || {
                currentPage: 1,
                totalPages: 1,
                limit: 10,
                totalRecords: 0
              };
            } else {
              this.showToast("error", res.data.message || "Failed to load employee transfers");
              this.transfers = [];
              this.summary = {};
            }
          })
          .catch((error) => {
            this.loading = false;
            console.error("API error:", error);
            this.showToast("error", "Network error when fetching transfers");
            this.transfers = [];
            this.summary = {};
          });
    },

    getDepartments() {
      const data = new FormData();
      data.append("function", "getDepartments");

      execute(data, authUrl)
          .then((res) => {
            if (res.data.success) {
              this.departments = res.data.data.departments || [];
            } else {
              console.error("Error fetching departments:", res.data.message);
            }
          })
          .catch((error) => {
            console.error("API error:", error);
          });
    },

    getEmployees() {
      const data = new FormData();
      data.append("function", "getEmployees");

      execute(data, authUrl)
          .then((res) => {
            if (res.data.success) {
              this.employees = res.data.data.employees || [];
            } else {
              console.error("Error fetching employees:", res.data.message);
            }
          })
          .catch((error) => {
            console.error("API error:", error);
          });
    },

    // Load employee data when selected in the transfer form
    loadEmployeeData() {
      if (!this.transferForm.employeeId) {
        this.selectedEmployee = null;
        return;
      }

      const data = new FormData();
      data.append("function", "getEmployeeForTransfer");
      data.append("employeeId", this.transferForm.employeeId);

      execute(data, authUrl)
          .then((res) => {
            if (res.data.success) {
              this.selectedEmployee = res.data.data.employee || null;

              if (this.selectedEmployee) {
                // Set the form values based on selected employee
                this.transferForm.fromDepartmentId = this.selectedEmployee.departmentID;
              }
            } else {
              this.showToast("error", res.data.message || "Failed to load employee data");
              this.selectedEmployee = null;
            }
          })
          .catch((error) => {
            console.error("API error:", error);
            this.showToast("error", "Network error when fetching employee data");
            this.selectedEmployee = null;
          });
    },

    // UI functions
    openNewTransferModal() {
      // Reset the form data
      this.transferForm = {
        employeeId: '',
        fromDepartmentId: '',
        toDepartmentId: '',
        transferDate: new Date().toISOString().slice(0, 10),
        effectiveDate: new Date().toISOString().slice(0, 10),
        reason: '',
        newPosition: '',
        newSalary: '',
        remarks: ''
      };
      this.selectedEmployee = null;
      this.showSalary = false;
      this.showFormSalary = false;

      // Open the modal
      new bootstrap.Modal(document.getElementById('newTransferModal')).show();
    },

    viewTransferDetails(transfer) {
      this.selectedTransfer = transfer;
      this.showNewSalary = false;
      new bootstrap.Modal(document.getElementById('transferDetailsModal')).show();
    },

    approveTransfer(transfer) {
      this.selectedTransfer = transfer;
      this.approvalForm.remarks = '';

      // Close details modal if open
      const detailsModal = bootstrap.Modal.getInstance(document.getElementById('transferDetailsModal'));
      if (detailsModal) {
        detailsModal.hide();
      }

      // Show the approve modal
      setTimeout(() => {
        new bootstrap.Modal(document.getElementById('approveTransferModal')).show();
      }, 500);
    },

    rejectTransfer(transfer) {
      this.selectedTransfer = transfer;
      this.rejectionForm.reason = '';
      this.rejectionError = '';

      // Close details modal if open
      const detailsModal = bootstrap.Modal.getInstance(document.getElementById('transferDetailsModal'));
      if (detailsModal) {
        detailsModal.hide();
      }

      // Show the reject modal
      setTimeout(() => {
        new bootstrap.Modal(document.getElementById('rejectTransferModal')).show();
      }, 500);
    },

    // Form submission handlers
    submitTransferForm() {
      // Validate form
      if (!this.transferForm.employeeId) {
        this.showToast("error", "Please select an employee");
        return;
      }

      if (!this.transferForm.toDepartmentId) {
        this.showToast("error", "Please select a target department");
        return;
      }

      if (this.transferForm.fromDepartmentId === this.transferForm.toDepartmentId) {
        this.showToast("error", "Target department must be different from current department");
        return;
      }

      if (!this.transferForm.reason) {
        this.showToast("error", "Please provide a reason for the transfer");
        return;
      }

      this.isSubmitting = true;

      const data = new FormData();
      data.append("function", "createEmployeeTransfer");
      data.append("employeeId", this.transferForm.employeeId);
      data.append("fromDepartmentId", this.transferForm.fromDepartmentId);
      data.append("toDepartmentId", this.transferForm.toDepartmentId);
      data.append("transferDate", this.transferForm.transferDate);
      data.append("effectiveDate", this.transferForm.effectiveDate);
      data.append("reason", this.transferForm.reason);

      if (this.transferForm.newPosition) {
        data.append("newPosition", this.transferForm.newPosition);
      }

      if (this.transferForm.newSalary) {
        data.append("newSalary", this.transferForm.newSalary);
      }

      if (this.transferForm.remarks) {
        data.append("remarks", this.transferForm.remarks);
      }

      execute(data, authUrl)
          .then((res) => {
            this.isSubmitting = false;

            if (res.data.success) {
              this.showToast("success", res.data.message || "Transfer request created successfully");

              // Close the modal and refresh data
              const modal = bootstrap.Modal.getInstance(document.getElementById('newTransferModal'));
              if (modal) {
                modal.hide();
              }

              // Refresh the transfers list
              this.refreshData();
            } else {
              this.showToast("error", res.data.message || "Failed to create transfer request");
            }
          })
          .catch((error) => {
            this.isSubmitting = false;
            console.error("API error:", error);
            this.showToast("error", "Network error when creating transfer request");
          });
    },

    confirmApproveTransfer() {
      if (!this.selectedTransfer) {
        return;
      }

      this.isProcessing = true;

      const data = new FormData();
      data.append("function", "approveEmployeeTransfer");
      data.append("transferId", this.selectedTransfer.id);

      if (this.approvalForm.remarks) {
        data.append("remarks", this.approvalForm.remarks);
      }

      execute(data, authUrl)
          .then((res) => {
            this.isProcessing = false;

            if (res.data.success) {
              this.showToast("success", res.data.message || "Transfer request approved successfully");

              // Close the modal and refresh data
              const modal = bootstrap.Modal.getInstance(document.getElementById('approveTransferModal'));
              if (modal) {
                modal.hide();
              }

              // Refresh the transfers list
              this.refreshData();
            } else {
              this.showToast("error", res.data.message || "Failed to approve transfer request");
            }
          })
          .catch((error) => {
            this.isProcessing = false;
            console.error("API error:", error);
            this.showToast("error", "Network error when approving transfer request");
          });
    },

    confirmRejectTransfer() {
      if (!this.selectedTransfer) {
        return;
      }

      // Validate the rejection reason
      if (!this.rejectionForm.reason.trim()) {
        this.rejectionError = "Rejection reason is required";
        return;
      }

      this.isProcessing = true;
      this.rejectionError = '';

      const data = new FormData();
      data.append("function", "rejectEmployeeTransfer");
      data.append("transferId", this.selectedTransfer.id);
      data.append("reason", this.rejectionForm.reason);

      execute(data, authUrl)
          .then((res) => {
            this.isProcessing = false;

            if (res.data.success) {
              this.showToast("success", res.data.message || "Transfer request rejected successfully");

              // Close the modal and refresh data
              const modal = bootstrap.Modal.getInstance(document.getElementById('rejectTransferModal'));
              if (modal) {
                modal.hide();
              }

              // Refresh the transfers list
              this.refreshData();
            } else {
              this.rejectionError = res.data.message || "Failed to reject transfer request";
            }
          })
          .catch((error) => {
            this.isProcessing = false;
            console.error("API error:", error);
            this.rejectionError = "Network error when rejecting transfer request";
          });
    },

    // Helper functions
    refreshData() {
      this.getTransfers();
    },

    resetFilters() {
      this.filters = {
        status: '',
        search: '',
        fromDate: '',
        toDate: '',
        departmentId: ''
      };
      this.pagination.currentPage = 1;
      this.getTransfers();
    },

    filterTransfers() {
      this.pagination.currentPage = 1; // Reset to first page when filtering
      this.getTransfers();
    },

    changePage(page) {
      if (page < 1 || page > this.pagination.totalPages || page === this.pagination.currentPage) {
        return;
      }

      this.pagination.currentPage = page;
      this.getTransfers();
    },

    getPageNumbers() {
      const totalPages = this.pagination.totalPages || 0;
      const currentPage = this.pagination.currentPage;

      if (totalPages <= 5) {
        return Array.from({ length: totalPages }, (_, i) => i + 1);
      }

      const pages = [1];

      if (currentPage <= 3) {
        pages.push(2, 3);
        if (totalPages > 3) pages.push('...', totalPages);
      } else if (currentPage >= totalPages - 2) {
        pages.push('...', totalPages - 2, totalPages - 1, totalPages);
      } else {
        pages.push('...', currentPage - 1, currentPage, currentPage + 1, '...', totalPages);
      }

      return pages;
    },

    getRowClass(status) {
      switch (status) {
        case 'Pending': return '';
        case 'Approved': return 'table-success-light';
        case 'Rejected': return 'table-danger-light';
        default: return '';
      }
    },

    getStatusBadgeClass(status) {
      switch (status) {
        case 'Pending': return 'bg-warning';
        case 'Approved': return 'bg-success';
        case 'Rejected': return 'bg-danger';
        default: return 'bg-secondary';
      }
    },

    getInitials(name) {
      if (!name) return '';
      return name.split(' ').map(part => part.charAt(0)).join('').toUpperCase();
    },

    formatDate(dateString, includeTime = false) {
      if (!dateString) return 'N/A';

      const date = new Date(dateString);
      if (isNaN(date)) return 'Invalid Date';

      const options = includeTime
          ? { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }
          : { year: 'numeric', month: 'short', day: 'numeric' };

      return date.toLocaleDateString('en-US', options);
    },

    formatCurrency(value,currency) {
      if (!value && value !== 0) return 'N/A';
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: currency,
        minimumFractionDigits: 2
      }).format(value);
    },

    showToast(type, message) {
      // Use toast notification plugin if available, otherwise use alert
      if (this.$toast) {
        this.$toast[type](message);
      } else {
        if (type === 'error') {
          alert('Error: ' + message);
        } else {
          alert(message);
        }
      }
    }
  }
};
</script>

<style scoped>
.avatar-xs {
  height: 2rem;
  width: 2rem;
  font-size: 1rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.avatar-sm {
  height: 3rem;
  width: 3rem;
}

.table-success-light {
  background-color: rgba(25, 135, 84, 0.1);
}

.table-danger-light {
  background-color: rgba(220, 53, 69, 0.1);
}

/* Badge colors */
.bg-success {
  background-color: #198754 !important;
}

.bg-warning {
  background-color: #ffc107 !important;
}

.bg-danger {
  background-color: #dc3545 !important;
}

.bg-info {
  background-color: #0dcaf0 !important;
}

.bg-primary {
  background-color: #0d6efd !important;
}

.bg-secondary {
  background-color: #6c757d !important;
}

.bg-light {
  background-color: #f8f9fa !important;
}

/* Font sizes */
.font-size-48 {
  font-size: 48px !important;
}

.font-size-24 {
  font-size: 24px !important;
}

.font-size-20 {
  font-size: 20px !important;
}

.font-size-18 {
  font-size: 18px !important;
}

.font-size-15 {
  font-size: 15px !important;
}

.font-size-14 {
  font-size: 14px !important;
}

input.salary-blur {
  filter: blur(5px);
  color: transparent;
  user-select: none;
}

input.salary-blur::placeholder {
  color: transparent;
}

.salary-blur {
  filter: blur(5px);
  padding: 0 25px;
  background-color: rgba(0, 0, 0, 0.1);
  border-radius: 4px;
  transition: all 0.3s ease;
}

.salary-blur:hover {
  filter: blur(4px);
  background-color: rgba(0, 0, 0, 0.15);
}

.cursor-pointer {
  cursor: pointer;
}
</style>